import { useCallback, useEffect, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { Box, TextField } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useRenameOrganizationMutation } from '../store/api';

interface OrganizationNameFieldProps {
  /** The current organization name from the server. */
  name: string;
}

/** Practice-admin rename of the organization (GDN-2441).
 *
 * The name is patient-facing (public booking page, confirmation messages),
 * so it saves on blur with the standard autosave affordance. Blank values
 * are never saved — the field snaps back to the server value instead.
 */
export function OrganizationNameField({ name }: OrganizationNameFieldProps) {
  const { plural } = useSubjectLanguageVariants();
  const [value, setValue] = useState(name);
  const [renameOrganization] = useRenameOrganizationMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { practiceSettings, error: errorTracking } = useEventTracking();

  // Track the server value so external refetches (or another admin's
  // rename) refresh an untouched field.
  useEffect(() => {
    setValue(name);
  }, [name]);

  const performSave = useCallback(
    async (nextName: string) => {
      try {
        await renameOrganization({
          updateOrganizationRequest: { name: nextName },
        }).unwrap();
        practiceSettings.organizationNameChanged();
      } catch (error) {
        enqueueSnackbar('Could not update the practice name', {
          variant: 'error',
        });
        errorTracking.track('Could not update the practice name', 'organization_rename');
        throw error;
      }
    },
    [renameOrganization, practiceSettings, enqueueSnackbar, errorTracking],
  );

  const { saveImmediately, status, lastSavedAt } = useAutoSave<string>({
    onSave: performSave,
  });

  const handleBlur = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) {
      // Blank names are patient-facing breakage; snap back instead.
      setValue(name);
      return;
    }
    setValue(trimmed);
    saveImmediately(trimmed);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <TextField
        label="Name"
        value={value}
        size="small"
        fullWidth
        onChange={event => setValue(event.target.value)}
        onBlur={handleBlur}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
          }
        }}
        slotProps={{ htmlInput: { maxLength: 120 } }}
        helperText={`Shown to ${plural} on your booking page and messages`}
      />
      <AutosaveStatus status={status} isDirty={value.trim() !== name} lastSavedAt={lastSavedAt} />
    </Box>
  );
}
